import { VERIFICATION_FIELDS } from '@/components/InsuranceSection/constants';
import { InsuranceFormValues, StringKeySignature } from '@/components/InsuranceSection/types';
import { InsurancePolicy as InsurancePolicyInterface } from '@/types/insurancePolicy';
import { InsuranceVerification } from '@/types/insuranceVerification';
import { currencyDollarsToCentsFormatter } from '@/utils/currencies';

/**
 * Takes the insurance section form values and reduces them into
 * separate insurance policy and verification fields keyed objects.
 *
 * Manipulates fields as necessary.
 * Converts deductible remaining to cents.
 *
 * @param values
 */
export const getSortedInsurancePolicyAndVerificationValues = (
  values: Partial<InsuranceFormValues>
): {
  insurancePolicyFields: Partial<InsurancePolicyInterface>;
  insuranceVerificationFields: Partial<InsuranceVerification>;
} => {
  const verificationFields = Object.values(VERIFICATION_FIELDS);
  return Object.keys(values).reduce(
    (acc, key) => {
      if (verificationFields.includes(key)) {
        if (key === VERIFICATION_FIELDS.new_verification_toggle) {
          return acc;
        }

        // [NOTE] - Deductible remaining is stored as cents and
        // needs to be converted before saving
        if (key === VERIFICATION_FIELDS.deductible_remaining) {
          (acc.insuranceVerificationFields as StringKeySignature)[
            VERIFICATION_FIELDS.deductible_remaining
          ] = (values[VERIFICATION_FIELDS.deductible_remaining] as number) * 100;

          return acc;
        }

        // * NOTE: All fields in BE should be cents
        // * Thus, we will use a common formatter
        // * for "cents2dollars" and "dollars2cents" mutations
        if (key === VERIFICATION_FIELDS.estimated_monthly_cost) {
          (acc.insuranceVerificationFields as StringKeySignature)[
            VERIFICATION_FIELDS.estimated_monthly_cost
          ] = currencyDollarsToCentsFormatter(
            values[VERIFICATION_FIELDS.estimated_monthly_cost] as number
          );

          return acc;
        }

        if (key === VERIFICATION_FIELDS.estimated_initial_month_cost) {
          (acc.insuranceVerificationFields as StringKeySignature)[
            VERIFICATION_FIELDS.estimated_initial_month_cost
          ] = currencyDollarsToCentsFormatter(
            values[VERIFICATION_FIELDS.estimated_initial_month_cost] as number
          );

          return acc;
        }

        (acc.insuranceVerificationFields as StringKeySignature)[
          key === VERIFICATION_FIELDS.status ? 'status' : key
        ] = values[key];
      } else {
        (acc.insurancePolicyFields as StringKeySignature)[key] = (values as StringKeySignature)[
          key
        ];
      }

      return acc;
    },
    { insurancePolicyFields: {}, insuranceVerificationFields: {} } as {
      insurancePolicyFields: Partial<InsurancePolicyInterface>;
      insuranceVerificationFields: Partial<InsuranceVerification>;
    }
  );
};
